import React from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import ListProposalCard from './ListProposalCard';
import {
  POOL_CLOSE_LABEL, POOL_EMPTY, POOL_END, POOL_INTRO, POOL_NOT_READY, POOL_NO_BATCH_NOTE,
  POOL_CAT_NOTE, POOL_FILTER_ALL, POOL_FILTER_CLEAR, POOL_FILTER_EMPTY, POOL_NOTE,
  POOL_RETRY_LABEL, POOL_SCOPE_ALL, POOL_SCOPE_NOTE, POOL_SCOPE_RELEVANT, POOL_SEASON_LABEL,
  POOL_SEASON_ORDER, POOL_TITLE,
  poolCatChips, poolFacetCount, poolMoreLabel, poolOpenLabel, poolProposal, poolRangeLine,
  poolSubTone, poolLinkIsFixable, poolVisible,
} from './counterList';
import { CounterPool } from './useCounterPool';
import { C, MONO, RADIUS } from './tokens';
import { RestockV2ListPoolItem, RestockV2ThumbTarget } from './types';

// 看更多 — 「引擎沒選，但你可以自己挑」.
//
// Magnus 2026-09-17, using the screen: 「建議加入 only have 2 items … i need more, show me
// suggestions and i will decide whether to add」. 建議加入 is capped by his own new_styles_per_week
// dial (8 → 2 proposals; 20 → 17; 30, the max → 19, where the evidence floor takes over), so the
// two cards he sees are the DIAL's opinion, not the catalogue's.
//
// This section is the rest of it, and everything about it is built to keep the two apart:
//   - it sits UNDER 建議加入 and is CLOSED by default, so it never stands between him and 我的清單;
//   - the strip, the intro and every card repeat 「引擎沒選」 — three chances to notice that these
//     rows are below the engine's bar and that he is overriding it knowingly;
//   - the cards are dashed, badged and carry no 引擎放 N 件, because the engine planned nothing here;
//   - the two verbs are the SAME two a proposal card has (加入清單 / 不要這款) hitting the same
//     endpoint, because the decision itself is identical once he has read the evidence;
//   - the 依據 is on the face of every card, never behind a fold (the 2026-09-15 correction);
//   - THERE IS NO 全部照建議 AND NO 全選, anywhere, ever. Every row here failed the engine's own
//     evidence test, and a bulk accept over rows nothing vouched for is the 2026-09-15 mistake —
//     16 evidence-free styles into a Korea order — wearing a friendlier button.

interface PoolSectionProps {
  pool: CounterPool;
  // SKUs decided somewhere ELSE on this screen (建議加入, the picker, 我的清單). This only ever
  // removes rows; nothing here can put one back.
  hidden: Record<string, true>;
  columns: number;
  onImage: (target: RestockV2ThumbTarget) => void;
}

const Strip: React.FC<{
  open: boolean; remaining: number; primed: boolean; loading: boolean; onToggle: () => void;
}> = ({ open, remaining, primed, loading, onToggle }) => (
  <Box
    component="button" type="button" onClick={onToggle} aria-expanded={open}
    aria-label={open ? `${POOL_CLOSE_LABEL} ${POOL_TITLE}` : poolOpenLabel(remaining)}
    sx={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 1, textAlign: 'left',
      appearance: 'none', cursor: 'pointer', bgcolor: C.wash, border: '1px dashed',
      borderColor: C.line2, borderRadius: `${RADIUS}px`, px: 1.25, py: 1.1, mt: 1.25,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography component="span" sx={{ display: 'block', fontSize: 14, fontWeight: 900, letterSpacing: '-.01em', color: C.ink }}>
        {POOL_TITLE}
        <Box component="span" sx={{ color: C.ink3, fontWeight: 700, fontSize: 12, ml: '6px' }}>{POOL_NOTE}</Box>
      </Typography>
      <Typography component="span" sx={{ display: 'block', fontSize: 11.5, color: C.ink2, mt: '2px' }}>
        {open ? POOL_NO_BATCH_NOTE : POOL_INTRO}
      </Typography>
    </Box>
    {loading ? (
      <CircularProgress size={14} sx={{ ml: 'auto', flex: 'none', color: C.ink3 }} />
    ) : (
      <Typography
        component="span"
        sx={{
          ml: 'auto', flex: 'none', fontSize: 12.5, fontWeight: 800, color: C.ink2,
          textDecoration: 'underline', textUnderlineOffset: '2px', whiteSpace: 'nowrap',
        }}
      >
        {open ? POOL_CLOSE_LABEL : poolOpenLabel(primed ? remaining : 0)}
      </Typography>
    )}
  </Box>
);

// The filter block: one scope toggle and two optional axes.
//
// 1,123 undecided styles is not a queue, it is a wall. Filters are what turn it into sittings —
// 「今天把 MdoC 的 17 款清掉」 is a job a man finishes; 「看完 1123 款」 is one he abandons.
//
// Every chip carries the count that TAPPING IT would return, served per response (`facets`) with
// the other axis and the scope held where they are. Nothing here is remembered and nothing is
// hardcoded: the pool moved 1144 → 1123 in the hour this was built, because decisions were landing
// on prod. A chip whose count is 0 is DISABLED rather than tappable into an empty list.
//
// The category chips are ordered by the rack he walks (衣架 → 褲架 → 配件層架 → MdoC 島台), which
// is also how `sourcing.ts` buckets those categories and where the pieces land when the box
// arrives. The chip text stays the server's own group name — that is the word he uses looking at a
// garment — and the rack order is stated once underneath instead of bolted onto six chips.
const Chip: React.FC<{
  text: string; count: number | null; active: boolean; busy: boolean; onClick: () => void;
  strong?: boolean;
}> = ({ text, count, active, busy, onClick, strong = false }) => {
  // 0 is a measured 「tapping this gives you nothing」; null is 「not measured」, which is not the
  // same claim and must not disable anything.
  const empty = count === 0;
  return (
    <Box
      component="button" type="button" onClick={onClick} disabled={busy || (empty && !active)}
      aria-pressed={active} aria-label={count === null ? text : `${text} ${count} 款`}
      sx={{
        appearance: 'none', cursor: 'pointer', borderRadius: '99px', px: 1.15, py: '5px',
        fontSize: 12.5, fontWeight: 800, whiteSpace: 'nowrap',
        border: '1px solid', borderColor: active ? C.ink : C.line2,
        bgcolor: active ? C.ink : C.card, color: active ? C.onInk : (strong ? C.ink : C.ink2),
        '&:disabled': { opacity: 0.4, cursor: 'default' },
      }}
    >
      {text}
      {count !== null && (
        <Box component="span" sx={{ fontFamily: MONO, ml: '5px', fontWeight: 700 }}>{count}</Box>
      )}
    </Box>
  );
};

const FilterRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, pb: 0.75 }}>
    <Typography
      component="span"
      sx={{ fontSize: 11.5, fontWeight: 800, color: C.ink3, flex: 'none', width: 34 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const Filters: React.FC<{ pool: CounterPool }> = ({ pool }) => {
  const busy = pool.loading || pool.loadingMore;
  const facet = (axis: 'season' | 'cat', key: string) => poolFacetCount(pool.facets, axis, key);
  return (
    <Box sx={{ px: '2px', pb: 0.5 }}>
      <FilterRow label="範圍">
        <Chip
          text={POOL_SCOPE_RELEVANT} count={pool.totalRelevant || null} strong
          active={pool.scope === 'relevant'} busy={busy}
          onClick={() => pool.setFilter({ scope: 'relevant' })}
        />
        <Chip
          text={POOL_SCOPE_ALL} count={pool.totalAll || null} strong
          active={pool.scope === 'all'} busy={busy}
          onClick={() => pool.setFilter({ scope: 'all' })}
        />
        <Typography sx={{ fontSize: 11.5, color: C.ink3, minWidth: 0, flex: '1 1 200px' }}>
          {POOL_SCOPE_NOTE}
        </Typography>
      </FilterRow>
      <FilterRow label="季節">
        <Chip
          text={POOL_FILTER_ALL} count={null} active={pool.season === null} busy={busy}
          onClick={() => pool.setFilter({ season: null })}
        />
        {POOL_SEASON_ORDER.map((key) => (
          <Chip
            key={`season:${key}`} text={POOL_SEASON_LABEL[key]} count={facet('season', key)}
            active={pool.season === key} busy={busy}
            onClick={() => pool.setFilter({ season: pool.season === key ? null : key })}
          />
        ))}
      </FilterRow>
      <FilterRow label="架子">
        <Chip
          text={POOL_FILTER_ALL} count={null} active={pool.cat === null} busy={busy}
          onClick={() => pool.setFilter({ cat: null })}
        />
        {poolCatChips(pool.rackGroups).map((name) => (
          <Chip
            key={`cat:${name}`} text={name} count={facet('cat', name)}
            active={pool.cat === name} busy={busy}
            onClick={() => pool.setFilter({ cat: pool.cat === name ? null : name })}
          />
        ))}
      </FilterRow>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 11.5, color: C.ink3, minWidth: 0 }}>{POOL_CAT_NOTE}</Typography>
        <Typography
          sx={{ fontSize: 12, fontWeight: 800, color: C.ink2, ml: 'auto', flex: 'none' }}
        >
          {poolRangeLine(pool.remaining)}
        </Typography>
        {pool.filtered && (
          <Box
            component="button" type="button" onClick={pool.clearFilter} disabled={busy}
            aria-label={POOL_FILTER_CLEAR}
            sx={{
              flex: 'none', appearance: 'none', background: 'none', border: 0, p: 0, fontSize: 12,
              fontWeight: 800, color: C.ink2, cursor: 'pointer', textDecoration: 'underline',
              textUnderlineOffset: '2px', '&:disabled': { opacity: 0.5, cursor: 'default' },
            }}
          >
            {POOL_FILTER_CLEAR}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const PoolSection: React.FC<PoolSectionProps> = ({ pool, hidden, columns, onImage }) => {
  // The backend has not shipped the pool, or the table is missing: one line, no controls. A button
  // that can only answer 503 teaches him the screen is broken.
  if (!pool.ready) {
    return (
      <Box component="section" aria-label={POOL_TITLE} sx={{ mt: 1.25 }}>
        <Typography role="note" sx={{ fontSize: 12, color: C.ink3, px: '2px' }}>
          {pool.note || POOL_NOT_READY}
        </Typography>
      </Box>
    );
  }

  const rows: RestockV2ListPoolItem[] = poolVisible(pool.items, {}, hidden);
  const shown = rows.length;

  return (
    <Box component="section" aria-label={POOL_TITLE}>
      <Strip
        open={pool.open} remaining={pool.remaining} primed={pool.primed} loading={pool.loading}
        onToggle={pool.toggle}
      />

      {pool.open && (
        <Box sx={{ pt: 1 }}>
          {/* Said again inside the section, because the strip scrolls out of view and a dashed card
              alone does not explain WHY it is dashed. */}
          <Typography sx={{ fontSize: 12, color: C.ink2, px: '2px', pb: 0.9, lineHeight: 1.5 }}>
            {POOL_INTRO}
          </Typography>
          <Filters pool={pool} />

          {pool.error && (
            <Alert
              severity="error" sx={{ mb: 1 }}
              action={(
                <Box
                  component="button" type="button" onClick={pool.loadMore} aria-label={POOL_RETRY_LABEL}
                  sx={{
                    appearance: 'none', background: 'none', border: 0, p: 0, fontSize: 12.5,
                    fontWeight: 800, color: 'inherit', cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  {POOL_RETRY_LABEL}
                </Box>
              )}
            >
              {pool.error}
            </Alert>
          )}

          {pool.loading && (
            <Box role="status" sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
          )}

          {/* Nothing to show is a SENTENCE, never a void: a filter that empties the list has to
              say so and offer the way back out, or the screen just looks broken. */}
          {!pool.loading && !shown && !pool.error && pool.primed && (
            <Box sx={{ px: '2px', py: 1 }}>
              <Typography sx={{ fontSize: 13, color: C.ink2 }}>
                {pool.filtered ? POOL_FILTER_EMPTY : POOL_EMPTY}
              </Typography>
              {pool.filtered && (
                <Box
                  component="button" type="button" onClick={pool.clearFilter}
                  aria-label={POOL_FILTER_CLEAR}
                  sx={{
                    mt: 0.75, appearance: 'none', cursor: 'pointer', borderRadius: '8px', px: 1.2,
                    py: '6px', fontSize: 12.5, fontWeight: 800, border: '1px solid',
                    borderColor: C.line2, bgcolor: C.card, color: C.ink2,
                  }}
                >
                  {POOL_FILTER_CLEAR}
                </Box>
              )}
            </Box>
          )}

          <Box
            sx={{
              display: 'grid', gap: 1, alignItems: 'stretch',
              ...(columns > 1
                ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
                : { '@media (min-width:600px)': { gridTemplateColumns: '1fr 1fr' } }),
            }}
          >
            {rows.map((item) => (
              <ListProposalCard
                key={`pool:${item.sku}`} row={poolProposal(item)} kind="pool"
                pending={!!pool.pending[item.sku]} subTone={poolSubTone(item)}
                fixableLink={poolLinkIsFixable(item)}
                onApprove={() => pool.decide(item, 'approve')}
                onDecline={() => pool.decide(item, 'reject')}
                onImage={onImage}
              />
            ))}
          </Box>

          {/* Batches, with the count on the button: he is never told 「more」 without being told how
              much more. Nothing auto-loads — the pool can be hundreds of rows. */}
          {shown > 0 && (
            <Box sx={{ mt: 1 }}>
              {pool.hasMore ? (
                <Box
                  component="button" type="button" onClick={pool.loadMore} disabled={pool.loadingMore}
                  aria-label={poolMoreLabel(shown, pool.remaining)}
                  sx={{
                    width: '100%', py: 1, cursor: 'pointer', border: '1px dashed', borderColor: C.line2,
                    bgcolor: C.wash, borderRadius: '9px', fontSize: 12.5, fontWeight: 800,
                    color: C.ink2, '&:disabled': { opacity: 0.55, cursor: 'default' },
                  }}
                >
                  {pool.loadingMore ? '載入中…' : poolMoreLabel(shown, pool.remaining)}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 12, color: C.ink3, px: '2px' }}>{POOL_END}</Typography>
              )}
              {/* Its own line. At 400 px a single row put 「11／457」 and 收起 hard against the right
                  edge and shaved the verb — the way OUT of the section must not be the one thing
                  the screen clips. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, px: '2px' }}>
                <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11.5, color: C.ink3 }}>
                  {shown}／{pool.remaining}
                </Typography>
                {/* A second 收起, at the BOTTOM. Measured 2026-09-17: 12 open cards put 我的清單
                    2,974 px down a 400 px screen — several swipes back up to the only other way
                    out. The way out belongs where the reading ends, not only where it started. */}
                <Box
                  component="button" type="button" onClick={pool.toggle}
                  aria-label={`${POOL_CLOSE_LABEL} ${POOL_TITLE}`}
                  sx={{
                    ml: 'auto', flex: 'none', appearance: 'none', background: 'none', border: 0, p: 0,
                    fontSize: 12.5, fontWeight: 800, color: C.ink2, cursor: 'pointer',
                    textDecoration: 'underline', textUnderlineOffset: '2px', whiteSpace: 'nowrap',
                  }}
                >
                  {POOL_CLOSE_LABEL}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PoolSection;
